import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Users, Plus, Trash2, Mail, Shield, Eye, ArrowLeft, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import { useLot } from '../contexts/LotContext';



interface LotAdmin {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'checker';
  status: 'active' | 'inactive' | 'invited';
  created_at: string;
  user_email: string;
  invited_by?: string;
  invited_at?: string;
}

export default function UserManagement() {
  const { lotSlug } = useParams<{ lotSlug: string }>();
  const { lot, userRole, isLoading: lotLoading } = useLot();
  const [admins, setAdmins] = useState<LotAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'admin' | 'checker'>('admin');
  const [inviting, setInviting] = useState(false);
  const [editingRole, setEditingRole] = useState<string | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'checker'>('admin');

  useEffect(() => {
    if (lot && !lotLoading) {
      fetchAdmins();
    }
  }, [lot, lotLoading]);

  const fetchAdmins = async () => {
    if (!lot) return;

    try {
      setLoading(true);
      
      // Get lot admins with user emails directly from the table
      const { data: adminData, error: adminError } = await supabase
        .from('lot_admins')
        .select('*')
        .eq('lot_id', lot.id)
        .order('created_at', { ascending: false });

      if (adminError) {
        console.error('Error fetching admins:', adminError);
        throw adminError;
      }

      // Get user IDs that are missing an email
      const userIdsWithMissingEmails = (adminData || [])
        .filter(admin => !admin.user_email)
        .map(admin => admin.user_id);

      let userEmailsMap: { [userId: string]: string } = {};

      // If there are admins with missing emails, fetch them from auth.users
      if (userIdsWithMissingEmails.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, email')
          .in('id', userIdsWithMissingEmails);

        if (usersError) {
          console.error('Error fetching user emails:', usersError);
          // Continue without emails if this fails, rather than blocking the whole page
        } else {
          usersData?.forEach(user => {
            userEmailsMap[user.id] = user.email;
          });
        }
      }
      
      const adminsWithEmails: LotAdmin[] = (adminData || []).map(admin => ({
        id: admin.id,
        user_id: admin.user_id,
        role: admin.role,
        status: admin.status,
        created_at: admin.created_at,
        user_email: admin.user_email || userEmailsMap[admin.user_id] || 'Unknown User',
        invited_by: admin.invited_by,
        invited_at: admin.invited_at,
      }));

      setAdmins(adminsWithEmails);
    } catch (error) {
      console.error('Error fetching lot admins:', error);
      toast.error('Failed to load team members');
    } finally {
      setLoading(false);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!lot || !inviteEmail.trim()) return;

    try {
      setInviting(true);

      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/invite-team-member'
        : '/.netlify/functions/invite-team-member';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lotId: lot.id,
          email: inviteEmail.trim(),
          role: inviteRole
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to invite team member');
      }

      toast.success(data.message);
      setShowInviteModal(false);
      setInviteEmail('');
      setInviteRole('admin');
      fetchAdmins();
    } catch (error) {
      console.error('Error inviting user:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to invite user');
    } finally {
      setInviting(false);
    }
  };

  const handleRemoveAdmin = async (adminId: string, userEmail: string) => {
    if (!confirm(`Are you sure you want to remove ${userEmail} from this lot?`)) {
      return;
    }

    // Find the admin record to get the user_id
    const adminRecord = admins.find(admin => admin.id === adminId);
    if (!adminRecord) {
      toast.error('Admin record not found');
      return;
    }

    try {
      console.log('Attempting to remove admin:', { adminId, userId: adminRecord.user_id, userEmail });
      
      const apiUrl = window.location.hostname === 'localhost'
        ? '/api/remove-team-member'
        : '/.netlify/functions/remove-team-member';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          adminId: adminId,
          userId: adminRecord.user_id,
          userEmail: userEmail
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.details || 'Failed to remove team member');
      }

      console.log('Remove team member result:', data);
      
      if (data.success) {
        toast.success(data.message);
        fetchAdmins();
      } else {
        toast.error('Failed to remove user');
      }
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to remove user');
    }
  };

  const handleRoleChange = async (adminId: string, userEmail: string, currentRole: string, newRole: 'admin' | 'checker') => {
    try {
      console.log('Updating role for admin:', adminId, 'from', currentRole, 'to', newRole);
      
      const { data, error } = await supabase
        .from('lot_admins')
        .update({ role: newRole })
        .eq('id', adminId)
        .select();

      if (error) {
        console.error('Error updating role:', error);
        throw error;
      }

      if (!data || data.length === 0) {
        console.warn('No records were updated. This might be a permissions issue.');
        toast.error('Failed to update role - insufficient permissions');
        return;
      }

      console.log('Successfully updated role:', data[0]);
      toast.success(`${userEmail} role updated to ${newRole}`);
      setEditingRole(null);
      fetchAdmins();
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Failed to update role');
    }
  };

  const startRoleEdit = (adminId: string, currentRole: 'admin' | 'checker') => {
    setEditingRole(adminId);
    setNewRole(currentRole);
  };

  const cancelRoleEdit = () => {
    setEditingRole(null);
    setNewRole('admin');
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'owner':
        return <Shield className="h-4 w-4 text-purple-600" />;
      case 'admin':
        return <Users className="h-4 w-4 text-blue-600" />;
      case 'checker':
        return <Eye className="h-4 w-4 text-green-600" />;
      default:
        return <Users className="h-4 w-4" />;
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'bg-purple-100 text-purple-800';
      case 'admin':
        return 'bg-blue-100 text-blue-800';
      case 'checker':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // Check permissions
  if (userRole !== 'owner' && userRole !== 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Access Denied
            </h2>
            <p className="text-gray-600 mb-4">
              You don't have permission to manage users for this lot.
            </p>
            <Link
              to={`/admin/lots/${lotSlug}/dashboard`}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (lotLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading team members...</p>
        </div>
      </div>
    );
  }

  if (!lot) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Lot Not Found</h2>
          <Link to="/admin" className="text-blue-600 hover:text-blue-800">
            Return to Admin
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center space-x-4">
              <Link
                to={`/admin/lots/${lotSlug}/dashboard`}
                className="text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="h-5 w-5" />
              </Link>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Team Management</h1>
                <p className="text-sm text-gray-600">{lot.display_name}</p>
              </div>
            </div>
            <button
              onClick={() => setShowInviteModal(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Add Team Member
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Team Members</h2>
            <p className="text-sm text-gray-600">
              Manage who has access to this parking lot's dashboard and settings.
            </p>
          </div>

          <div className="divide-y divide-gray-200">
            {admins.length === 0 ? (
              <div className="p-6 text-center">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No team members</h3>
                <p className="text-gray-600">
                  Add team members to help manage this parking lot.
                </p>
              </div>
            ) : (
              admins.map((admin) => (
                <div key={admin.id} className="p-6 flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center space-x-2">
                      {getRoleIcon(admin.role)}
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-medium text-gray-900">
                            {admin.user_email}
                          </span>
                          {editingRole === admin.id ? (
                            <div className="flex items-center space-x-2">
                              <select
                                value={newRole}
                                onChange={(e) => setNewRole(e.target.value as 'admin' | 'checker')}
                                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                              >
                                <option value="admin">Admin</option>
                                <option value="checker">Checker</option>
                              </select>
                              <button
                                onClick={() => handleRoleChange(admin.id, admin.user_email, admin.role, newRole)}
                                className="text-green-600 hover:text-green-800 text-xs"
                                title="Save role change"
                              >
                                ✓
                              </button>
                              <button
                                onClick={cancelRoleEdit}
                                className="text-gray-600 hover:text-gray-800 text-xs"
                                title="Cancel"
                              >
                                ✕
                              </button>
                            </div>
                          ) : (
                            <div className="flex items-center space-x-1">
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRoleColor(admin.role)}`}>
                                {admin.role.charAt(0).toUpperCase() + admin.role.slice(1)}
                              </span>
                              {admin.role !== 'owner' && (userRole === 'owner' || userRole === 'admin') && (
                                <button
                                  onClick={() => startRoleEdit(admin.id, admin.role as 'admin' | 'checker')}
                                  className="text-gray-400 hover:text-gray-600 ml-1"
                                  title="Change role"
                                >
                                  <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">
                          Added on {new Date(admin.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    {admin.role !== 'owner' && (
                      <button
                        onClick={() => handleRemoveAdmin(admin.id, admin.user_email)}
                        className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50 transition-colors"
                        title="Remove user"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Role Descriptions */}
        <div className="mt-8 bg-white shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Role Permissions</h3>
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-purple-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Owner</div>
                <div className="text-sm text-gray-600">
                  Full access to everything including team management, settings, and payments.
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Users className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Admin</div>
                <div className="text-sm text-gray-600">
                  Can manage parking sessions, view analytics, process refunds, and modify settings.
                </div>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <Eye className="h-5 w-5 text-green-600 mt-0.5" />
              <div>
                <div className="font-medium text-gray-900">Checker</div>
                <div className="text-sm text-gray-600">
                  Read-only access to parking sessions. Cannot see financial data or customer info.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Add Team Member</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>

            <form onSubmit={handleInviteUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Enter email address"
                    disabled={inviting}
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  If the user doesn't have an account, one will be created and login credentials will be provided.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Role
                </label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as 'admin' | 'checker')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={inviting}
                >
                  <option value="admin">Admin - Full access</option>
                  <option value="checker">Checker - Read-only access</option>
                </select>
              </div>

              <div className="flex space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  disabled={inviting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviting}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {inviting ? 'Adding...' : 'Add Member'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 